import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import { MailCheck, Mail, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { COMPANY } from "@/lib/compliance";
import { clearSession, getSession, requestMagicLink } from "@/lib/portalApi";

/**
 * /customer-portal/login — magic-link sign-in.
 *
 * Visitor types their email. We POST to /api/portal/auth/request which is
 * idempotent and never reveals whether the email is registered. On success
 * we show a "check your email" confirmation state.
 */
export default function CustomerPortalLoginPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | submitting | sent | error
    const [error, setError] = useState(null);

    useEffect(() => {
        document.title = "Customer Portal Sign-in — Endurance Casualty";
        // If a valid-looking session exists, send them straight to the dashboard
        const s = getSession();
        if (s?.token) navigate("/customer-portal/dashboard", { replace: true });
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) {
            setError("Please enter a valid email address.");
            return;
        }
        setError(null);
        setStatus("submitting");
        try {
            await requestMagicLink(email.trim().toLowerCase());
            setStatus("sent");
        } catch (_) {
            setStatus("error");
            setError(
                "We couldn't send a sign-in link right now. Please try again, or call " +
                    COMPANY.phone +
                    "."
            );
        }
    };

    return (
        <main data-testid="page-portal-login" className="min-h-screen bg-brand-bg text-brand-ivory">
            <PageHeader />
            <section className="mx-auto max-w-3xl px-6 py-20 md:px-10 md:py-24">
                <div className="rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-8 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)] md:p-10">
                    <p className="text-[11px] uppercase tracking-[0.45em] text-brand-goldSoft">
                        Secure Customer Portal
                    </p>
                    <h1 className="mt-3 font-serif text-3xl leading-tight text-brand-ivory md:text-4xl">
                        Sign in with a magic link
                    </h1>
                    <p className="mt-3 text-base leading-relaxed text-brand-muted">
                        Enter the email address on file with{" "}
                        {COMPANY.shortName}. We&apos;ll email you a one-time
                        secure link that signs you straight into your
                        document portal — no password required.
                    </p>

                    {status !== "sent" && (
                        <form
                            data-testid="portal-login-form"
                            onSubmit={handleSubmit}
                            className="mt-8 space-y-4"
                        >
                            <label
                                htmlFor="portal-email"
                                className="block text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft"
                            >
                                Email address
                            </label>
                            <div className="relative">
                                <Mail
                                    aria-hidden="true"
                                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-goldSoft"
                                />
                                <input
                                    id="portal-email"
                                    data-testid="portal-login-email"
                                    type="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="w-full rounded-lg border border-[rgba(212,175,55,0.35)] bg-[#0d1117] px-10 py-3 text-sm text-brand-ivory placeholder-brand-muted/60 focus:border-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                                />
                            </div>

                            {error && (
                                <p
                                    data-testid="portal-login-error"
                                    role="alert"
                                    className="rounded-md border border-red-500/40 bg-red-500/5 px-3 py-2 text-xs text-red-200"
                                >
                                    {error}
                                </p>
                            )}

                            <button
                                type="submit"
                                data-testid="portal-login-submit"
                                disabled={status === "submitting"}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-bg shadow-[0_18px_36px_-18px_rgba(212,175,55,0.6)] transition-all hover:bg-brand-gold2 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                            >
                                {status === "submitting" ? (
                                    <>
                                        <Loader2
                                            aria-hidden="true"
                                            className="h-4 w-4 animate-spin"
                                        />
                                        Sending…
                                    </>
                                ) : (
                                    <>
                                        <ShieldCheck
                                            aria-hidden="true"
                                            className="h-4 w-4"
                                        />
                                        Send my sign-in link
                                        <ArrowRight
                                            aria-hidden="true"
                                            className="h-4 w-4"
                                        />
                                    </>
                                )}
                            </button>
                        </form>
                    )}

                    {status === "sent" && (
                        <div
                            data-testid="portal-login-sent"
                            className="mt-8 rounded-xl border border-[rgba(212,175,55,0.35)] bg-[#0d1117] p-5"
                        >
                            <div className="flex items-start gap-3">
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-brand-gold/50 bg-[#0b0e13]">
                                    <MailCheck
                                        aria-hidden="true"
                                        className="h-5 w-5 text-brand-gold2"
                                    />
                                </span>
                                <div>
                                    <p className="font-serif text-lg text-brand-ivory">
                                        Check your email.
                                    </p>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                        If <strong>{email}</strong> is
                                        registered with {COMPANY.shortName}, a
                                        secure sign-in link is on its way.
                                        Click the link within the next 20
                                        minutes to access your portal.
                                    </p>
                                    <p className="mt-3 text-xs text-brand-muted">
                                        Didn&apos;t receive it? Check spam, or{" "}
                                        <button
                                            type="button"
                                            data-testid="portal-login-resend"
                                            onClick={() => {
                                                setStatus("idle");
                                                clearSession();
                                            }}
                                            className="text-brand-gold2 underline-offset-4 hover:underline"
                                        >
                                            try again
                                        </button>
                                        .
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <p className="mt-8 text-[11px] text-brand-muted">
                        Not yet a client?{" "}
                        <Link
                            to="/contact"
                            className="text-brand-gold2 underline-offset-4 hover:underline"
                        >
                            Contact us
                        </Link>
                        . Need to pay a premium instead?{" "}
                        <Link
                            to="/pay-premium"
                            className="text-brand-gold2 underline-offset-4 hover:underline"
                        >
                            Pay premium →
                        </Link>
                    </p>
                </div>
            </section>
            <FooterBar />
        </main>
    );
}
